import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { gaPageView } from './utils/analytics';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { NotificationProvider } from './context/NotificationContext';
import Login from './components/Login/Login';
import ResetPassword from './components/Login/ResetPassword';
import CreatePassword from './components/Login/CreatePassword';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import TermsOfService from './components/Legal/TermsOfService';
import SuperAdminDashboard from './components/Dashboard/SuperAdminDashboard';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import EmployeeDashboard from './components/Dashboard/EmployeeDashboard';
import AccountantDashboard from './components/Dashboard/AccountantDashboard';
import CustomerDashboard from './components/Dashboard/CustomerDashboard';
import AirExportInsights from './components/Dashboard/AirExportInsights';
import NewBooking from './components/Dashboard/NewBooking';
import AddUserPage from './components/Dashboard/AddUserPage';
import ProfilePage from './components/Profile/ProfilePage';
import ShipmentDetails from './components/Dashboard/ShipmentDetails';
import SeaShipmentDetails from './components/Dashboard/SeaShipmentDetails';
import Footer from './components/common/Footer';
import { initEmailJS } from './utils/emailService';
import { WalletProvider } from './context/WalletContext';

// Fires gaPageView on every route change
const GAPageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const titles = {
      '/':          'Home',
      '/super-admin':'Super Admin Dashboard',
      '/admin':     'Admin Dashboard',
      '/employee':  'Employee Dashboard',
      '/accountant':'Accountant Dashboard',
      '/user':      'Customer Dashboard',
      '/user/insights': 'Export Insights',
      '/user/air-export-insights': 'Air Export Insights',
      '/profile':   'Profile',
    };
    const title = titles[location.pathname] || document.title;
    gaPageView(location.pathname + location.search, title);
  }, [location]);
  return null;
};

// Domain constants
const LOGIN_DOMAIN = 'client.hmrspedition.com';
const DASHBOARD_DOMAIN = 'quicklook.in';

const AppContent = () => {
  const { user, loading, allUsers, setAllUsers, getToken } = useAuth();
  const currentHost = window.location.hostname;
  const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1';

  // Production: real domains check | Local: same opened URL pe single-app flow
  const isLoginDomain = currentHost === LOGIN_DOMAIN;
  const isDashboardDomain = currentHost === DASHBOARD_DOMAIN || currentHost === `www.${DASHBOARD_DOMAIN}` || isLocalhost;

  const handleAddUser = (newUser) => {
    setAllUsers([newUser, ...allUsers]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // ─── LOGIN DOMAIN (client.hmrspedition.com) ───
  // Agar login domain pe hai aur user already logged in hai → _auth token ke saath dashboard pe bhejo
  // (Direct redirect without token = loop kyunki dashboard domain ka localStorage alag hota hai)
  if (isLoginDomain) {
    if (user) {
      const rawToken = getToken();
      const payload = JSON.stringify({ user, token: rawToken });
      const encoded = btoa(btoa(encodeURIComponent(payload)));
      window.location.href = `https://${DASHBOARD_DOMAIN}/?_auth=${encoded}`;
      return null;
    }
    return <Login />;
  }

  // ─── DASHBOARD DOMAIN (quicklook.in) ───
  // Agar dashboard domain pe hai aur user logged in nahi → login page pe bhejo
  if (isDashboardDomain && !user) {
    if (!isLocalhost) {
      // We know there's no session here, so tell the login domain not to
      // bounce back for another check.
      window.location.href = `https://${LOGIN_DOMAIN}/?_from=quicklook`;
      return null;
    }
    return <Login />;
  }

  if (!user) {
    return <Login />;
  }

  // Route based on user role
  console.log('✅ Logged in user role:', user.role, '| Full user:', user);
  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <GAPageTracker />
      <div className="flex-1">
        <Routes>
          <Route path="/" element={
            user.role === 'super_admin' ? <Navigate to="/super-admin" /> :
            user.role === 'admin' ? <Navigate to="/admin" /> :
            user.role === 'employee' ? <Navigate to="/employee" /> :
            user.role === 'accountant' ? <Navigate to="/accountant" /> :
            user.role === 'user' ? <Navigate to="/user" /> :
            <Navigate to="/" />
          } />
          
          {user.role === 'super_admin' && (
            <>
              <Route path="/super-admin" element={<SuperAdminDashboard allUsers={allUsers} setAllUsers={setAllUsers} />} />
              <Route path="/admin/add-user" element={<AddUserPage allUsers={allUsers} onAddUser={handleAddUser} />} />
            </>
          )}
          
          {user.role === 'admin' && (
            <>
              <Route path="/admin" element={<AdminDashboard allUsers={allUsers} setAllUsers={setAllUsers} />} />
              <Route path="/admin/add-user" element={<AddUserPage allUsers={allUsers} onAddUser={handleAddUser} />} />
            </>
          )}
          
          {user.role === 'employee' && (
            <Route path="/employee" element={<EmployeeDashboard />} />
          )}
          
          {user.role === 'accountant' && (
            <Route path="/accountant" element={<AccountantDashboard />} />
          )}
          
          {user.role === 'user' && (
            <>
              <Route path="/user" element={<CustomerDashboard />} />
              <Route path="/user/insights" element={<AirExportInsights />} />
              <Route path="/user/air-export-insights" element={<AirExportInsights />} />
              <Route path="/user/new-booking" element={<NewBooking />} />
            </>
          )}

          {/* Profile Page - accessible to all logged-in users */}
          <Route path="/profile" element={<ProfilePage />} />
          
          {/* Shipment Details Page - accessible to all logged-in users */}
          <Route path="/shipment/:awbNo" element={<ShipmentDetails />} />
          <Route path="/sea-shipment/:blNumber" element={<SeaShipmentDetails />} />
          
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
      <Footer tone="auto" className="py-4" />
    </div>
  );
};

function App() {
  // Initialize EmailJS on app load
  useEffect(() => {
    initEmailJS();
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <WalletProvider>
          <NotificationProvider>
          <Toaster 
            position="top-right"
            reverseOrder={false}
            toastOptions={{
              duration: 3000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <Routes>
            {/* Public Routes - No Auth Required */}
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/create-password" element={<CreatePassword />} />
            {/* Privacy & Terms — reachable by URL; not linked from anywhere yet */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            
            {/* Protected Routes */}
            <Route path="/*" element={<AppContent />} />
          </Routes>
          </NotificationProvider>
          </WalletProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
